import React, { useEffect, useMemo, useState } from "react";
import {
  startOfMonth, startOfDay, format, eachDayOfInterval,
  differenceInCalendarDays, addDays
} from "date-fns";

import { useAuth } from "./auth/AuthContext";
import { db } from "./firebase";

import {
  addDoc, collection, doc, getDocs, onSnapshot, orderBy,
  query, serverTimestamp, where, setDoc, runTransaction
} from "firebase/firestore";

import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer
} from "recharts";
import CelebrationOverlay from "./components/CelebrationOverlay";

const monthStartISO = startOfMonth(new Date()).toISOString();

function ProgressBar({ value, max, label }) {
  const pct = Math.max(0, Math.min(100, (value / (max || 1)) * 100));
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
        <span>{label}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div style={{ height: 10, background: "#eee", borderRadius: 6 }}>
        <div style={{ width: `${pct}%`, height: 10, background: "#4caf50", borderRadius: 6 }} />
      </div>
    </div>
  );
}

export default function NaNo() {
  const { user, logout } = useAuth();
  const [title, setTitle] = useState("");
  const [goalWords, setGoalWords] = useState(50000);
  const [goalDays, setGoalDays] = useState(30);
  const [books, setBooks] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState(null);
  const [makePublic, setMakePublic] = useState(true); // opt-in leaderboard
  const [busyId, setBusyId] = useState(null);
  const [goalCelebration, setGoalCelebration] = useState(null);

  const booksCol = useMemo(() => collection(db, "nanoking_books"), []);

  // subscribe to my books (live)
  useEffect(() => {
    if (!user) return;
    setStatus(null);
    const qy = query(booksCol, where("ownerUid", "==", user.uid));
    const unsub = onSnapshot(
      qy,
      snap => setBooks(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      err => { console.error("[books]", err); setStatus(`${err.code}: ${err.message}`); }
    );
    return () => unsub();
  }, [user, booksCol]);

  const createBook = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    const total = Math.max(1, Number(goalWords) || 50000);
    const days = Math.max(1, Number(goalDays) || 30);
    const daily = Math.ceil(total / days);
    const startDate = startOfDay(new Date()).toISOString();
    try {
      await addDoc(booksCol, {
        title: title.trim(),
        ownerUid: user.uid,
        ownerName: user.displayName || user.email,
        currentCount: 0,
        targetTotal: total,
        targetDays: days,
        dailyGoal: daily,
        startDate,
        monthStart: monthStartISO,
        public: makePublic,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setTitle("");
      setGoalWords(50000);
      setGoalDays(30);
      setStatus(null);
    } catch (err) {
      console.error("[createBook]", err);
      setStatus(`createBook: ${err.code || "error"} — ${err.message}`);
    }
  };

  // Sum currentCount across my books and upsert public row
  const updatePublicStatsSum = async () => {
    if (!user) return;
    const snap = await getDocs(query(booksCol, where("ownerUid", "==", user.uid)));
    let total = 0, anyPublic = false;
    snap.forEach(d => { const x = d.data(); total += Number(x.currentCount || 0); if (x.public) anyPublic = true; });
    if (!anyPublic) return; // nothing to show publicly
    await setDoc(doc(db, "nanoking_public_stats", user.uid), {
      uid: user.uid,
      name: user.displayName || user.email,
      totalCount: total,
      updatedAt: serverTimestamp()
    }, { merge: true });
  };

  // Transactional incremental update:
  // - increments book.currentCount
  // - upserts updates/{YYYY-MM-DD} with cumulative + today's delta (summed)
  // - refreshes public stats as sum of all books
  const updateCount = async (book) => {
    if (!book) return;
    const bookId = book.id;
    const raw = counts[bookId] ?? "";
    const delta = parseInt(raw, 10);
    if (Number.isNaN(delta) || delta <= 0) return;
    const dailyGoal = Number(book.dailyGoal || Math.ceil((book.targetTotal || 50000) / (book.targetDays || 30)));
    const hitsGoalThreshold = delta >= dailyGoal;

    setBusyId(bookId);
    try {
      const bookRef = doc(db, "nanoking_books", bookId);
      const todayKey = format(startOfDay(new Date()), "yyyy-MM-dd");
      const todayRef = doc(bookRef, "updates", todayKey);

      await runTransaction(db, async (tx) => {
        const bookSnap = await tx.get(bookRef);
        if (!bookSnap.exists()) throw new Error("book not found");
        const prevTotal = Number(bookSnap.data().currentCount || 0);
        const newTotal = prevTotal + delta;

        const todaySnap = await tx.get(todayRef);
        const prevDelta = todaySnap.exists() ? Number(todaySnap.data().delta || 0) : 0;
        const newDelta = prevDelta + delta;

        // upsert today's per-day doc
        tx.set(todayRef, {
          at: serverTimestamp(),
          delta: newDelta,      // words written today (accumulated if multiple updates)
          count: newTotal,      // cumulative total after this update
          source: "manual-increment"
        }, { merge: true });

        // authoritative running total on the book
        tx.update(bookRef, {
          currentCount: newTotal,
          updatedAt: serverTimestamp()
        });
      });

      if (hitsGoalThreshold) {
        setGoalCelebration({
          message: "Daily goal crushed!",
          subtext: `+${delta.toLocaleString()} words logged`
        });
      }

      setCounts((s) => ({ ...s, [bookId]: "" }));
      setStatus(null);

      // keep public leaderboard in sync (sum across all books)
      await updatePublicStatsSum();
    } catch (err) {
      console.error("[updateCount tx]", err);
      setStatus(`updateCount: ${err.code || "error"} — ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ maxWidth: 920, margin: "32px auto", padding: "0 16px" }}>
      {goalCelebration && (
        <CelebrationOverlay
          message={goalCelebration.message}
          subtext={goalCelebration.subtext}
          onClose={() => setGoalCelebration(null)}
        />
      )}
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1>NaNoKingORamaThon</h1>
        <div>
          <span style={{ marginRight: 12 }}>{user?.displayName || user?.email}</span>
          <button onClick={logout}>Sign out</button>
        </div>
      </header>

      {status && (
        <div style={{ background: "#fee", border: "1px solid #f88", padding: 12, borderRadius: 8, marginTop: 16, color: "#600" }}>
          {status}
        </div>
      )}

      <section style={{ marginTop: 24, padding: 16, border: "1px solid #ddd", borderRadius: 12 }}>
        <h3>Create a book</h3>
        <form onSubmit={createBook} style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr repeat(2, 140px) auto", alignItems: "center" }}>
          <input
            placeholder="Book title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ padding: 8 }}
          />
          <input
            type="number"
            min="1"
            placeholder="Total words"
            value={goalWords}
            onChange={(e) => setGoalWords(Number(e.target.value) || 0)}
            style={{ padding: 8 }}
          />
          <input
            type="number"
            min="1"
            placeholder="Days"
            value={goalDays}
            onChange={(e) => setGoalDays(Number(e.target.value) || 0)}
            style={{ padding: 8 }}
          />
          <button type="submit">Add</button>
          <label style={{ gridColumn: "1 / -1", fontSize: 13 }}>
            <input type="checkbox" checked={makePublic} onChange={(e)=>setMakePublic(e.target.checked)} /> Add me to the public leaderboard
          </label>
        </form>
      </section>

      <section style={{ marginTop: 24 }}>
        {books.length === 0 ? <p>No books yet.</p> : (
          <div style={{ display: "grid", gap: 16 }}>
            {books.map(b => (
              <BookCard
                key={b.id}
                book={b}
                counts={counts}
                setCounts={setCounts}
                onUpdate={() => updateCount(b)}
                busy={busyId === b.id}
              />
            ))}
          </div>
        )}
      </section>

      <Leaderboard />
    </div>
  );
}

function BookCard({ book, counts, setCounts, onUpdate, busy }) {
  const [updates, setUpdates] = useState([]);
  const bookRef = useMemo(() => doc(db, "nanoking_books", book.id), [book.id]);

  // live history for this book (per-day docs)
  useEffect(() => {
    const qy = query(collection(bookRef, "updates"), orderBy("at", "asc"));
    const unsub = onSnapshot(qy, snap => setUpdates(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => unsub();
  }, [bookRef]);

  // Chart setup based on custom start/duration
  const target = Number(book.targetTotal || 50000);
  const targetDays = Number(book.targetDays || 30);
  const dailyGoal = Number(book.dailyGoal || Math.ceil(target / targetDays));

  const startDate = startOfDay(book.startDate ? new Date(book.startDate) : startOfMonth(new Date()));
  const endDate = addDays(startDate, Math.max(1, targetDays) - 1);
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const daysSpan = Math.max(1, differenceInCalendarDays(endDate, startDate) + 1);

  // Build map of cumulative totals by day (prefer 'count' from per-day docs)
  const byDay = new Map();
  for (const u of updates) {
    const key = format(startOfDay((u.at?.toDate?.() || new Date())), "yyyy-MM-dd");
    const cum = Number(u.count || 0);
    byDay.set(key, Math.max(byDay.get(key) || 0, cum));
  }

  // Authoritative total from book
  const total = Number(book.currentCount || 0);

  // Ensure today reflects current authoritative total
  const todayKey = format(startOfDay(new Date()), "yyyy-MM-dd");
  byDay.set(todayKey, Math.max(byDay.get(todayKey) || 0, total));

  // Build lines
  let last = 0;
  const data = days.map((d, idx) => {
    const k = format(startOfDay(d), "yyyy-MM-dd");
    const val = (byDay.get(k) ?? (idx === 0 ? 0 : last));
    last = val;
    const ideal = Math.round((target * (idx + 1)) / daysSpan); // straight line to target
    return { day: format(d, "MM-dd"), total: val, ideal };
  });

  // Metrics
// Which day is "today" on the chart (0-based, robust to TZ/DST)
const dayOfMonth = new Date().getDate();
const clampedIndex = Math.max(0, Math.min(dayOfMonth - 1, data.length - 1));

// Yesterday's cumulative (for fallback)
const yesterdayTotal = clampedIndex > 0 ? data[clampedIndex - 1].total : 0;

// --- NEW: prefer today's per-day delta from updates ---
const writtenTodayFromUpdates = updates
  .filter(u => format(startOfDay((u.at?.toDate?.() || new Date())), "yyyy-MM-dd") === todayKey)
  .reduce((sum, u) => sum + Number(u.delta || 0), 0);

// Use the stored delta if present; otherwise fall back to diff of totals
const todayDelta = writtenTodayFromUpdates > 0
  ? writtenTodayFromUpdates
  : Math.max(0, total - yesterdayTotal);

// Ideal cumulative for today and ahead/behind
const todayIdeal = data[clampedIndex]?.ideal ?? 0;
const aheadBehind = total - todayIdeal;

  return (
    <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: 0 }}>{book.title}</h3>

          <div style={{ fontSize: 13, opacity: 0.85 }}>
            Total: <b>{total.toLocaleString()}</b> / {target.toLocaleString()} &nbsp;•&nbsp;
            Today: <b>{todayDelta}</b> / {dailyGoal} &nbsp;•&nbsp;
            {aheadBehind >= 0 ? (
              <span style={{ color: "#2e7d32" }}>Ahead by {aheadBehind.toLocaleString()}</span>
            ) : (
              <span style={{ color: "#c62828" }}>Behind by {Math.abs(aheadBehind).toLocaleString()}</span>
            )}
          </div>

          <ProgressBar value={todayDelta} max={dailyGoal} label="Today" />
          <ProgressBar value={total} max={target} label="Goal total" />
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            type="number"
            placeholder="Words written today"
            value={counts[book.id] ?? ""}
            onChange={(e) => setCounts(s => ({ ...s, [book.id]: e.target.value }))}
            style={{ padding: 8, width: 180 }}
            disabled={busy}
          />
          <button onClick={onUpdate} disabled={busy || !counts[book.id]}>
            {busy ? "Saving…" : "Update"}
          </button>
        </div>
      </div>

      <div style={{ height: 240, marginTop: 16 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" minTickGap={18} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            {/* Actual cumulative (solid) vs ideal straight line (dashed) */}
            <Line type="monotone" dataKey="total" dot={false} />
            <Line type="monotone" dataKey="ideal" strokeDasharray="6 6" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Leaderboard() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    const qy = query(collection(db, "nanoking_public_stats"), orderBy("totalCount", "desc"));
    const unsub = onSnapshot(qy, snap => setRows(snap.docs.map(d => d.data())));
    return () => unsub();
  }, []);
  if (rows.length === 0) return null;
  return (
    <section style={{ marginTop: 32, padding: 16, border: "1px solid #ddd", borderRadius: 12 }}>
      <h3>Public Leaderboard</h3>
      <ol>
        {rows.map(r => (
          <li key={r.uid}>
            <b>{r.name}</b> — {r.totalCount?.toLocaleString() ?? 0}
          </li>
        ))}
      </ol>
    </section>
  );
}
